import React, { useEffect, useState } from 'react';
import {
  RiFileListLine as ListIcon,
  RiSparklingLine as SparklesIcon,
  RiDeleteBinLine as DeleteIcon,
  RiFileTextLine as DocumentIcon,
} from 'react-icons/ri';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Card from '@/components/ui/Card';
import ApiService from '@/api/cases';
import ExhibitListViewer from './ExhibitListViewer';
import { analytics } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/utils/analytics-events';

interface ExhibitListsTabProps {
  caseId: string;
}

type Mode = 'list' | 'view';

interface ExhibitList {
  id: string;
  caseId: string;
  createdAt: string;
  title: string;
  summary: string;
  visaType: string;
  totalExhibits: number;
  organizationStrategy?: string;
  recommendations?: string[];
  generatedBy: string;
  criteriaGroups: {
    tableOfContents?: string[]; // Array of exhibit titles
    exhibits: Array<{
      exhibitNumber: string;
      documentId: string;
      fileName: string;
      description: string;
      criteriaIds?: string[];
    }>;
  };
}

const ExhibitListsTab: React.FC<ExhibitListsTabProps> = ({ caseId }) => {
  const [mode, setMode] = useState<Mode>('list');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<string>('');
  const [exhibitLists, setExhibitLists] = useState<ExhibitList[]>([]);
  const [selectedList, setSelectedList] = useState<ExhibitList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // New state for enhanced generation options
  const [showGenerateOptions, setShowGenerateOptions] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [selectedFormats, setSelectedFormats] = useState({
    standard: true,
    detailed: false,
    condensed: false,
  });
  const [formatType, setFormatType] = useState<
    'TABLE_OF_CONTENTS' | 'EXHIBIT_TABS' | 'PAGE_NUMBER_INDICATION'
  >('TABLE_OF_CONTENTS');
  const [numberingScheme, setNumberingScheme] = useState<
    'NUMERIC' | 'ALPHA_UPPER' | 'ALPHA_LOWER' | 'ROMAN_UPPER' | 'ROMAN_LOWER' | 'HIERARCHICAL'
  >('NUMERIC');
  const [includeTableOfContents, setIncludeTableOfContents] = useState(true);
  const [includePageNumbers, setIncludePageNumbers] = useState(true);

  useEffect(() => {
    loadExhibitLists();
  }, [caseId]);

  const loadExhibitLists = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await ApiService.getExhibitLists(caseId);
      if (response.success && response.data) {
        setExhibitLists(
          Array.isArray(response.data) ? response.data : [response.data]
        );
      } else {
        setExhibitLists([]);
      }
    } catch (err: any) {
      console.error('Failed to load exhibit lists:', err);
      setError(err.message || 'Failed to load exhibit lists');
      setExhibitLists([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateExhibitList = async () => {
    try {
      setGenerating(true);
      setError(null);
      setSuccess(null);

      // Show progress updates
      setGenerationProgress('Loading documents and criteria...');
      setTimeout(
        () => setGenerationProgress('Analyzing document relevance...'),
        2000
      );
      setTimeout(
        () => setGenerationProgress('Generating exhibit descriptions...'),
        10000
      );
      setTimeout(
        () =>
          setGenerationProgress(
            'Organizing exhibits and creating table of contents...'
          ),
        30000
      );
      setTimeout(
        () => setGenerationProgress('Finalizing exhibit list...'),
        45000
      );

      // Determine which format to use
      const format = selectedFormats.detailed
        ? 'detailed'
        : selectedFormats.condensed
          ? 'condensed'
          : 'standard';

      analytics.track(ANALYTICS_EVENTS.EXHIBIT_LIST_GENERATION_SUBMITTED, {
        case_id: caseId,
        format,
        format_type: formatType,
        numbering_scheme: numberingScheme,
        include_toc: includeTableOfContents,
        include_page_numbers: includePageNumbers,
        has_custom_prompt: !!customPrompt.trim(),
      });

      const response = await ApiService.generateExhibitList(caseId, {
        customPrompt: customPrompt.trim() || undefined,
        format,
        formatType,
        numberingScheme,
        includeTableOfContents,
      });

      if (response.success) {
        setGenerationProgress('');
        setSuccess('Exhibit list generated successfully!');
        await loadExhibitLists();
        setShowGenerateOptions(false); // Hide options after successful generation
        setCustomPrompt(''); // Clear custom prompt
      } else {
        throw new Error(response.message || 'Failed to generate exhibit list');
      }
    } catch (err: any) {
      console.error('Failed to generate exhibit list:', err);
      setError(err.message || 'Failed to generate exhibit list');
      setGenerationProgress('');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (exhibitListId: string) => {
    if (!confirm('Are you sure you want to delete this exhibit list?')) {
      return;
    }

    try {
      await ApiService.deleteExhibitList(caseId, exhibitListId);
      setSuccess('Exhibit list deleted successfully');
      await loadExhibitLists();
    } catch (err: any) {
      console.error('Failed to delete exhibit list:', err);
      setError(err.message || 'Failed to delete exhibit list');
    }
  };

  const handleDownload = async (
    exhibitListId: string,
    format: 'pdf' | 'docx' | 'markdown' = 'pdf'
  ) => {
    try {
      const blob = await ApiService.downloadExhibitList(
        caseId,
        exhibitListId,
        format
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `exhibit-list-${exhibitListId}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setSuccess(`Exhibit list downloaded as ${format.toUpperCase()}`);
    } catch (err: any) {
      console.error('Failed to download exhibit list:', err);
      setError(err.message || 'Failed to download exhibit list');
    }
  };

  // View mode - show exhibit list viewer
  if (mode === 'view' && selectedList) {
    return (
      <ExhibitListViewer
        exhibitList={selectedList}
        caseId={caseId}
        onBack={() => {
          setSelectedList(null);
          setMode('list');
          loadExhibitLists();
        }}
      />
    );
  }

  if (loading && !generating) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Show generation progress
  if (generating && generationProgress) {
    return (
      <div className="space-y-6">
        <Card padding="lg">
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <LoadingSpinner size="lg" />
            <div className="text-center">
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                Generating Exhibit List
              </h3>
              <p className="text-sm text-neutral-600">{generationProgress}</p>
              <p className="text-xs text-neutral-500 mt-2">
                This may take 30-60 seconds...
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (exhibitLists.length === 0) {
    return (
      <div className="space-y-6">
        {/* Messages */}
        {error && (
          <Card padding="sm">
            <div className="text-sm text-error-600">{error}</div>
          </Card>
        )}
        {success && (
          <Card padding="sm">
            <div className="text-sm text-success-600">{success}</div>
          </Card>
        )}

        {/* Empty State with Enhanced Generation Options */}
        <Card padding="lg">
          <div className="text-center py-12">
            <div className="flex flex-col items-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-primary-100 flex items-center justify-center">
                <ListIcon className="h-8 w-8 text-primary-600" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-neutral-900">
                  No Exhibit Lists Yet
                </h3>
                <p className="text-neutral-500 text-sm max-w-md mx-auto">
                  Generate an AI-powered exhibit list to organize your documents
                  by visa criteria. The backend analyzes your documents and
                  creates a structured exhibit list for your petition.
                </p>
              </div>

              {/* Generation Options Dropdown */}
              {!showGenerateOptions ? (
                <Button
                  onClick={() => {
                    setShowGenerateOptions(true);
                    analytics.track(
                      ANALYTICS_EVENTS.EXHIBIT_LIST_GENERATE_CLICKED,
                      {
                        case_id: caseId,
                        source: 'empty_state',
                      }
                    );
                  }}
                  className="flex items-center space-x-2"
                >
                  <SparklesIcon className="h-4 w-4" />
                  <span>Generate Exhibit List</span>
                </Button>
              ) : (
                <div className="w-full max-w-2xl space-y-4">
                  {/* Format Selection Checkboxes */}
                  <div className="border border-neutral-200 rounded-lg px-4 py-3 text-left">
                    <h4 className="font-medium text-neutral-700 mb-3">
                      Select Format
                    </h4>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="format"
                          checked={selectedFormats.standard}
                          onChange={() =>
                            setSelectedFormats({
                              standard: true,
                              detailed: false,
                              condensed: false,
                            })
                          }
                          className="w-4 h-4 text-primary-600"
                        />
                        <span className="text-sm text-neutral-700">
                          <strong>Standard Format</strong> - Professional
                          exhibit list with table of contents and detailed
                          descriptions
                        </span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="format"
                          checked={selectedFormats.detailed}
                          onChange={() =>
                            setSelectedFormats({
                              standard: false,
                              detailed: true,
                              condensed: false,
                            })
                          }
                          className="w-4 h-4 text-primary-600"
                        />
                        <span className="text-sm text-neutral-700">
                          <strong>Detailed Format</strong> - Extended
                          descriptions with more context and analysis
                        </span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="format"
                          checked={selectedFormats.condensed}
                          onChange={() =>
                            setSelectedFormats({
                              standard: false,
                              detailed: false,
                              condensed: true,
                            })
                          }
                          className="w-4 h-4 text-primary-600"
                        />
                        <span className="text-sm text-neutral-700">
                          <strong>Condensed Format</strong> - Brief descriptions
                          for simple cases
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Format Type Selection */}
                  <div className="border border-neutral-200 rounded-lg px-4 py-3 text-left">
                    <h4 className="font-medium text-neutral-700 mb-3">
                      Organization Style
                    </h4>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="formatType-empty"
                          checked={formatType === 'TABLE_OF_CONTENTS'}
                          onChange={() => setFormatType('TABLE_OF_CONTENTS')}
                          className="w-4 h-4 text-primary-600"
                        />
                        <span className="text-sm text-neutral-700">
                          <strong>Table of Contents</strong> - Index at
                          beginning with descriptions
                        </span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="formatType-empty"
                          checked={formatType === 'EXHIBIT_TABS'}
                          onChange={() => setFormatType('EXHIBIT_TABS')}
                          className="w-4 h-4 text-primary-600"
                        />
                        <span className="text-sm text-neutral-700">
                          <strong>Exhibit Tabs</strong> - Physical tabs with
                          cover pages for each section
                        </span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="formatType-empty"
                          checked={formatType === 'PAGE_NUMBER_INDICATION'}
                          onChange={() =>
                            setFormatType('PAGE_NUMBER_INDICATION')
                          }
                          className="w-4 h-4 text-primary-600"
                        />
                        <span className="text-sm text-neutral-700">
                          <strong>Page Numbers</strong> - Inline references
                          (e.g., "See page 12, Exhibit II")
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Numbering Scheme Selection */}
                  <div className="border border-neutral-200 rounded-lg px-4 py-3 text-left">
                    <h4 className="font-medium text-neutral-700 mb-3">
                      Numbering Scheme
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="numberingScheme-empty"
                          checked={numberingScheme === 'NUMERIC'}
                          onChange={() => setNumberingScheme('NUMERIC')}
                          className="w-4 h-4 text-primary-600"
                        />
                        <span className="text-sm text-neutral-700">
                          <strong>1, 2, 3...</strong>
                        </span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="numberingScheme-empty"
                          checked={numberingScheme === 'ALPHA_UPPER'}
                          onChange={() => setNumberingScheme('ALPHA_UPPER')}
                          className="w-4 h-4 text-primary-600"
                        />
                        <span className="text-sm text-neutral-700">
                          <strong>A, B, C...</strong>
                        </span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="numberingScheme-empty"
                          checked={numberingScheme === 'ALPHA_LOWER'}
                          onChange={() => setNumberingScheme('ALPHA_LOWER')}
                          className="w-4 h-4 text-primary-600"
                        />
                        <span className="text-sm text-neutral-700">
                          <strong>a, b, c...</strong>
                        </span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="numberingScheme-empty"
                          checked={numberingScheme === 'ROMAN_UPPER'}
                          onChange={() => setNumberingScheme('ROMAN_UPPER')}
                          className="w-4 h-4 text-primary-600"
                        />
                        <span className="text-sm text-neutral-700">
                          <strong>I, II, III...</strong>
                        </span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="numberingScheme-empty"
                          checked={numberingScheme === 'ROMAN_LOWER'}
                          onChange={() => setNumberingScheme('ROMAN_LOWER')}
                          className="w-4 h-4 text-primary-600"
                        />
                        <span className="text-sm text-neutral-700">
                          <strong>i, ii, iii...</strong>
                        </span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer col-span-2">
                        <input
                          type="radio"
                          name="numberingScheme-empty"
                          checked={numberingScheme === 'HIERARCHICAL'}
                          onChange={() => setNumberingScheme('HIERARCHICAL')}
                          className="w-4 h-4 text-primary-600"
                        />
                        <span className="text-sm text-neutral-700">
                          <strong>A-1, A-2, B-1...</strong> (Hierarchical by criteria)
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Additional Options */}
                  <div className="border border-neutral-200 rounded-lg px-4 py-3 text-left">
                    <h4 className="font-medium text-neutral-700 mb-3">
                      Additional Options
                    </h4>
                    <div className="space-y-3">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={includeTableOfContents}
                          onChange={e =>
                            setIncludeTableOfContents(e.target.checked)
                          }
                          className="w-4 h-4 text-primary-600 rounded border-neutral-300 focus:ring-primary-500"
                        />
                        <span className="text-sm text-neutral-700">
                          <strong>Include Table of Contents</strong> - Add an
                          index at the beginning listing all exhibits
                        </span>
                      </label>
                      <p className="text-xs text-neutral-500 ml-6">
                        Uncheck this if you reference exhibits directly in your
                        cover letter and don't need a separate index.
                      </p>

                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={includePageNumbers}
                          onChange={e =>
                            setIncludePageNumbers(e.target.checked)
                          }
                          className="w-4 h-4 text-primary-600 rounded border-neutral-300 focus:ring-primary-500"
                        />
                        <span className="text-sm text-neutral-700">
                          <strong>Include Page Numbers</strong> - Show page
                          ranges for each exhibit (e.g., "Pages 1-5")
                        </span>
                      </label>
                      <p className="text-xs text-neutral-500 ml-6">
                        Display page numbers to help USCIS officers locate
                        documents in the physical petition package.
                      </p>
                    </div>
                  </div>

                  {/* Custom Prompt Text Area */}
                  <div className="border border-neutral-200 rounded-lg px-4 py-3 text-left">
                    <label className="block font-medium text-neutral-700 mb-2">
                      Additional Instructions (Optional)
                    </label>
                    <textarea
                      value={customPrompt}
                      onChange={e => setCustomPrompt(e.target.value)}
                      placeholder="Add any specific instructions for the AI (e.g., 'Emphasize research publications', 'Group by project timeline', etc.)"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      rows={3}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-center space-x-3">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setShowGenerateOptions(false);
                        setCustomPrompt('');
                        setSelectedFormats({
                          standard: true,
                          detailed: false,
                          condensed: false,
                        });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleGenerateExhibitList}
                      disabled={generating}
                      className="flex items-center space-x-2"
                    >
                      {generating ? (
                        <>
                          <LoadingSpinner size="sm" />
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          <SparklesIcon className="h-4 w-4" />
                          <span>Generate Exhibit List</span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Render exhibit lists
  return (
    <div className="space-y-6">
      {/* Messages */}
      {error && (
        <Card padding="sm">
          <div className="text-sm text-error-600">{error}</div>
        </Card>
      )}
      {success && (
        <Card padding="sm">
          <div className="text-sm text-success-600">{success}</div>
        </Card>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-neutral-900">
          Exhibit Lists ({exhibitLists.length})
        </h2>
        <Button
          onClick={() => {
            setShowGenerateOptions(!showGenerateOptions);
            if (!showGenerateOptions) {
              analytics.track(ANALYTICS_EVENTS.EXHIBIT_LIST_GENERATE_CLICKED, {
                case_id: caseId,
                source: 'header_button',
              });
            }
          }}
          size="sm"
          className="flex items-center space-x-2"
        >
          <SparklesIcon className="h-4 w-4" />
          <span>Generate New List</span>
        </Button>
      </div>

      {/* Enhanced Generation Options (shown when button clicked) */}
      {showGenerateOptions && (
        <Card padding="md">
          <div className="space-y-4">
            {/* Example Exhibit List Dropdown */}
            <div className="border border-neutral-200 rounded-lg">
              <button
                type="button"
                onClick={() => setShowExample(!showExample)}
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-neutral-50 transition-colors"
              >
                <span className="font-medium text-neutral-700">
                  View Example Exhibit List
                </span>
                <span className="text-neutral-400">
                  {showExample ? '▲' : '▼'}
                </span>
              </button>
              {showExample && (
                <div className="px-4 py-3 border-t border-neutral-200 bg-neutral-50 text-left">
                  <div className="prose prose-sm max-w-none text-neutral-700">
                    <h4 className="font-semibold">
                      INDEX OF SUPPORTING EVIDENCE
                    </h4>
                    <p className="text-sm italic">
                      In order to satisfy the burden of proof for O-1A
                      classification, we offer the following evidence:
                    </p>
                    <div className="mt-3 space-y-1 text-sm">
                      <p>Exhibit 1: Curriculum Vitae of Dr. John Doe</p>
                      <p>
                        Exhibit 2: Supporting Letter from Dr. Jane Smith,
                        Professor of Computer Science, MIT
                      </p>
                      <p>
                        Exhibit 3: Publication - "Neural Architecture Search"
                        (Nature, 2023)
                      </p>
                      <p>
                        Exhibit 4: Award Certificate - IEEE Outstanding
                        Achievement Award
                      </p>
                      <p>
                        Exhibit 5: Media Coverage - TechCrunch Feature Article
                      </p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-neutral-300">
                      <p className="font-semibold">
                        Exhibit 1: Curriculum Vitae of Dr. John Doe
                      </p>
                      <p className="text-sm mt-2">
                        This is Dr. John Doe's comprehensive curriculum vitae
                        detailing his extensive experience in artificial
                        intelligence and machine learning. The CV demonstrates
                        his progression from graduate research at MIT through
                        his current role as Principal Research Scientist at
                        TechCorp...
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Format Selection Checkboxes */}
            <div className="border border-neutral-200 rounded-lg px-4 py-3 text-left">
              <h4 className="font-medium text-neutral-700 mb-3">
                Select Format
              </h4>
              <div className="space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="format"
                    checked={selectedFormats.standard}
                    onChange={() =>
                      setSelectedFormats({
                        standard: true,
                        detailed: false,
                        condensed: false,
                      })
                    }
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-sm text-neutral-700">
                    <strong>Standard Format</strong> - Professional exhibit list
                    with table of contents and detailed descriptions
                  </span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="format"
                    checked={selectedFormats.detailed}
                    onChange={() =>
                      setSelectedFormats({
                        standard: false,
                        detailed: true,
                        condensed: false,
                      })
                    }
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-sm text-neutral-700">
                    <strong>Detailed Format</strong> - Extended descriptions
                    with more context and analysis
                  </span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="format"
                    checked={selectedFormats.condensed}
                    onChange={() =>
                      setSelectedFormats({
                        standard: false,
                        detailed: false,
                        condensed: true,
                      })
                    }
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-sm text-neutral-700">
                    <strong>Condensed Format</strong> - Brief descriptions for
                    simple cases
                  </span>
                </label>
              </div>
            </div>

            {/* Format Type Selection */}
            <div className="border border-neutral-200 rounded-lg px-4 py-3 text-left">
              <h4 className="font-medium text-neutral-700 mb-3">
                Organization Style
              </h4>
              <div className="space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="formatType"
                    checked={formatType === 'TABLE_OF_CONTENTS'}
                    onChange={() => setFormatType('TABLE_OF_CONTENTS')}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-sm text-neutral-700">
                    <strong>Table of Contents</strong> - Index at beginning with
                    descriptions
                  </span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="formatType"
                    checked={formatType === 'EXHIBIT_TABS'}
                    onChange={() => setFormatType('EXHIBIT_TABS')}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-sm text-neutral-700">
                    <strong>Exhibit Tabs</strong> - Physical tabs with cover
                    pages for each section
                  </span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="formatType"
                    checked={formatType === 'PAGE_NUMBER_INDICATION'}
                    onChange={() => setFormatType('PAGE_NUMBER_INDICATION')}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-sm text-neutral-700">
                    <strong>Page Numbers</strong> - Inline references (e.g.,
                    "See page 12, Exhibit II")
                  </span>
                </label>
              </div>
            </div>

            {/* Numbering Scheme Selection */}
            <div className="border border-neutral-200 rounded-lg px-4 py-3 text-left">
              <h4 className="font-medium text-neutral-700 mb-3">
                Numbering Scheme
              </h4>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="numberingScheme"
                    checked={numberingScheme === 'NUMERIC'}
                    onChange={() => setNumberingScheme('NUMERIC')}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-sm text-neutral-700">
                    <strong>1, 2, 3...</strong>
                  </span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="numberingScheme"
                    checked={numberingScheme === 'ALPHA_UPPER'}
                    onChange={() => setNumberingScheme('ALPHA_UPPER')}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-sm text-neutral-700">
                    <strong>A, B, C...</strong>
                  </span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="numberingScheme"
                    checked={numberingScheme === 'ALPHA_LOWER'}
                    onChange={() => setNumberingScheme('ALPHA_LOWER')}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-sm text-neutral-700">
                    <strong>a, b, c...</strong>
                  </span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="numberingScheme"
                    checked={numberingScheme === 'ROMAN_UPPER'}
                    onChange={() => setNumberingScheme('ROMAN_UPPER')}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-sm text-neutral-700">
                    <strong>I, II, III...</strong>
                  </span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="numberingScheme"
                    checked={numberingScheme === 'ROMAN_LOWER'}
                    onChange={() => setNumberingScheme('ROMAN_LOWER')}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-sm text-neutral-700">
                    <strong>i, ii, iii...</strong>
                  </span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer col-span-2">
                  <input
                    type="radio"
                    name="numberingScheme"
                    checked={numberingScheme === 'HIERARCHICAL'}
                    onChange={() => setNumberingScheme('HIERARCHICAL')}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-sm text-neutral-700">
                    <strong>A-1, A-2, B-1...</strong> (Hierarchical by criteria)
                  </span>
                </label>
              </div>
            </div>

            {/* Additional Options */}
            <div className="border border-neutral-200 rounded-lg px-4 py-3 text-left">
              <h4 className="font-medium text-neutral-700 mb-3">
                Additional Options
              </h4>
              <div className="space-y-3">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeTableOfContents}
                    onChange={e => setIncludeTableOfContents(e.target.checked)}
                    className="w-4 h-4 text-primary-600 rounded border-neutral-300 focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-700">
                    <strong>Include Table of Contents</strong> - Add an index at
                    the beginning listing all exhibits
                  </span>
                </label>
                <p className="text-xs text-neutral-500 ml-6">
                  Uncheck this if you reference exhibits directly in your cover
                  letter and don't need a separate index.
                </p>

                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includePageNumbers}
                    onChange={e => setIncludePageNumbers(e.target.checked)}
                    className="w-4 h-4 text-primary-600 rounded border-neutral-300 focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-700">
                    <strong>Include Page Numbers</strong> - Show page ranges for
                    each exhibit (e.g., "Pages 1-5")
                  </span>
                </label>
                <p className="text-xs text-neutral-500 ml-6">
                  Display page numbers to help USCIS officers locate documents
                  in the physical petition package.
                </p>
              </div>
            </div>

            {/* Custom Prompt Text Area */}
            <div className="border border-neutral-200 rounded-lg px-4 py-3 text-left">
              <label className="block font-medium text-neutral-700 mb-2">
                Additional Instructions (Optional)
              </label>
              <textarea
                value={customPrompt}
                onChange={e => setCustomPrompt(e.target.value)}
                placeholder="Add any specific instructions for the AI (e.g., 'Emphasize research publications', 'Group by project timeline', etc.)"
                className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows={3}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowGenerateOptions(false);
                  setCustomPrompt('');
                  setSelectedFormats({
                    standard: true,
                    detailed: false,
                    condensed: false,
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleGenerateExhibitList}
                disabled={generating}
                className="flex items-center space-x-2"
              >
                {generating ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <SparklesIcon className="h-4 w-4" />
                    <span>Generate Exhibit List</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Exhibit Lists */}
      <div className="space-y-4">
        {exhibitLists.map(list => (
          <Card key={list.id} padding="md">
            {/* List Header */}
            <div
              className={`flex items-start justify-between ${exhibitLists.length > 1 ? 'mb-4 pb-4 border-b border-neutral-200' : ''}`}
            >
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <ListIcon className="h-5 w-5 text-primary-600" />
                  <h3 className="text-base font-semibold text-neutral-900">
                    {list.title || 'Exhibit List'}
                  </h3>
                </div>
                <div className="text-sm text-neutral-500 space-y-1">
                  <p>Generated: {new Date(list.createdAt).toLocaleString()}</p>
                  <p>
                    {list.totalExhibits} exhibits
                    {list.visaType && ` • ${list.visaType}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedList(list);
                    setMode('view');
                  }}
                  className="flex items-center space-x-1"
                >
                  <DocumentIcon className="h-4 w-4" />
                  <span>Open</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(list.id)}
                  className="flex items-center space-x-1 text-error-600 hover:text-error-700"
                >
                  <DeleteIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ExhibitListsTab;
